import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthStore } from './auth'
import { logger } from '@/lib/logger'
import { useSpotifyNodesStore, type SpotifyNode } from './spotifyNodes'
import { useSpotifyStore } from './spotify'
import { getDataProvider } from '@/lib/dataProvider'

// TypeScript interfaces based on the database schema
export interface SceneSpotifyConfig {
  id: string
  scene_id: string
  spotify_node_id: string
  spotify_url: string
  fade_duration: number
  created_at: string
  updated_at: string
}

export interface CreateSceneSpotifyConfigData {
  scene_id: string
  spotify_node_id: string
  spotify_url: string
  fade_duration?: number
}

export interface UpdateSceneSpotifyConfigData {
  spotify_url?: string
  fade_duration?: number
}

// Resolved config with Spotify node info for display purposes
export interface ResolvedSceneSpotifyConfig {
  id: string
  scene_id: string
  spotify_node_id: string
  spotify_url: string
  fade_duration: number
  created_at: string
  updated_at: string
  // Spotify node info
  spotify_node_name: string
  spotify_node: SpotifyNode | null // Will be populated with SpotifyNode data
  // Spotify metadata
  spotify_image_url?: string | null
  spotify_metadata?: Record<string, unknown> | null
}

export const useSceneSpotifyConfigsStore = defineStore('sceneSpotifyConfigs', () => {
  // State
  const configs = ref<Map<string, SceneSpotifyConfig>>(new Map())
  const resolvedConfigs = ref<Map<string, ResolvedSceneSpotifyConfig>>(new Map())
  const loadingStates = ref<Map<string, boolean>>(new Map())
  const error = ref<string | null>(null)
  const isLoading = ref(false)

  // Getters
  const getConfigsForScene = computed(() => (sceneId: string) => {
    return Array.from(configs.value.values())
      .filter(config => config.scene_id === sceneId)
  })

  const getResolvedConfigsForScene = computed(() => (sceneId: string) => {
    return Array.from(resolvedConfigs.value.values())
      .filter(config => config.scene_id === sceneId)
  })

  const getConfigForSceneAndNode = computed(() => (sceneId: string, nodeId: string) => {
    return Array.from(configs.value.values())
      .find(config => config.scene_id === sceneId && config.spotify_node_id === nodeId)
  })

  const getConfigById = computed(() => (id: string) => {
    return configs.value.get(id)
  })

  const isConfigLoading = computed(() => (id: string) => {
    return loadingStates.value.get(id) || false
  })

  // Helper to resolve configs with Spotify node data
  const resolveConfigs = async (sceneConfigs: SceneSpotifyConfig[]): Promise<ResolvedSceneSpotifyConfig[]> => {
    const spotifyNodesStore = useSpotifyNodesStore()
    const spotifyStore = useSpotifyStore()
    
    const resolvedConfigs = await Promise.all(sceneConfigs.map(async config => {
      const spotifyNode = spotifyNodesStore.getSpotifyNodeById(config.spotify_node_id)
      
      // Fetch metadata from Spotify API
      let spotifyImageUrl = null
      let spotifyMetadata = null
      
      if (spotifyStore.isConnected && config.spotify_url) {
        try {
          const urlInfo = spotifyNodesStore.parseSpotifyUrl(config.spotify_url)
          if (urlInfo && ['track', 'playlist'].includes(urlInfo.type)) {
            const metadata = await spotifyStore.getTrackOrPlaylist(urlInfo.type as 'track' | 'playlist', urlInfo.id)
            if (metadata) {
              spotifyMetadata = metadata
              if (urlInfo.type === 'track' && metadata.album?.images?.[0]) {
                spotifyImageUrl = metadata.album.images[0].url
              } else if (urlInfo.type === 'playlist' && metadata.images?.[0]) {
                spotifyImageUrl = metadata.images[0].url
              }
            }
          }
        } catch (error) {
          logger.warn('Failed to fetch Spotify metadata for config:', config.id, error)
        }
      }
      
      return {
        ...config,
        spotify_node_name: spotifyMetadata?.name || spotifyNode?.name || 'Unknown',
        spotify_node: spotifyNode || null,
        spotify_image_url: spotifyImageUrl,
        spotify_metadata: spotifyMetadata
      }
    }))
    
    return resolvedConfigs
  }

  // Actions
  const fetchConfigsForScene = async (sceneId: string): Promise<{ success: boolean; error?: string }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    isLoading.value = true
    error.value = null

    try {
      const provider = getDataProvider()
      const sceneConfigs = await provider.query<SceneSpotifyConfig>('scene_spotify_configs', {
        filters: [{ column: 'scene_id', operator: 'eq', value: sceneId }]
      })

      // Update store with configs for this scene
      sceneConfigs.forEach(config => {
        configs.value.set(config.id, config)
      })

      // Resolve configs with Spotify node data
      const resolved = await resolveConfigs(sceneConfigs)
      
      // Update resolved configs
      resolved.forEach(resolvedConfig => {
        resolvedConfigs.value.set(resolvedConfig.id, resolvedConfig)
      })

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      error.value = errorMessage
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  const createConfig = async (data: CreateSceneSpotifyConfigData): Promise<{ success: boolean; error?: string; data?: SceneSpotifyConfig }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    const createData = {
      scene_id: data.scene_id,
      spotify_node_id: data.spotify_node_id,
      spotify_url: data.spotify_url,
      fade_duration: data.fade_duration || 10.0
    }

    try {
      const provider = getDataProvider()
      const result = await provider.insert<SceneSpotifyConfig>('scene_spotify_configs', createData)

      if (!result.success || !result.data) {
        return { success: false, error: result.error }
      }

      // Update store
      configs.value.set(result.data.id, result.data)

      // Update resolved configs
      const resolved = await resolveConfigs([result.data])
      if (resolved.length > 0) {
        resolvedConfigs.value.set(resolved[0].id, resolved[0])
      }

      return { success: true, data: result.data }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  const updateConfig = async (id: string, data: UpdateSceneSpotifyConfigData): Promise<{ success: boolean; error?: string; data?: SceneSpotifyConfig }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    loadingStates.value.set(id, true)

    try {
      const provider = getDataProvider()
      const result = await provider.update<SceneSpotifyConfig>('scene_spotify_configs', id, data)

      if (!result.success || !result.data) {
        return { success: false, error: result.error }
      }

      // Update store
      configs.value.set(id, result.data)

      // Update resolved configs
      const resolved = await resolveConfigs([result.data])
      if (resolved.length > 0) {
        resolvedConfigs.value.set(resolved[0].id, resolved[0])
      }

      return { success: true, data: result.data }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    } finally {
      loadingStates.value.set(id, false)
    }
  }

  const deleteConfig = async (id: string): Promise<{ success: boolean; error?: string }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    loadingStates.value.set(id, true)

    try {
      const provider = getDataProvider()
      const result = await provider.delete('scene_spotify_configs', id)

      if (!result.success) {
        return { success: false, error: result.error }
      }

      // Update store
      configs.value.delete(id)
      resolvedConfigs.value.delete(id)
      loadingStates.value.delete(id)

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    } finally {
      loadingStates.value.set(id, false)
    }
  }

  const removeConfigForSceneAndNode = async (sceneId: string, nodeId: string): Promise<{ success: boolean; error?: string }> => {
    const config = getConfigForSceneAndNode.value(sceneId, nodeId)
    
    if (!config) {
      return { success: false, error: 'Config not found' }
    }

    return await deleteConfig(config.id)
  }

  /**
   * Link a Spotify node to a scene (upsert)
   */
  const linkToScene = async (spotifyNodeId: string, sceneId: string, spotifyUrl: string): Promise<{ success: boolean; error?: string }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    try {
      const provider = getDataProvider()
      const result = await provider.upsert<SceneSpotifyConfig>('scene_spotify_configs', {
        scene_id: sceneId,
        spotify_node_id: spotifyNodeId,
        spotify_url: spotifyUrl,
        fade_duration: 10.0
      }, 'scene_id,spotify_node_id')

      if (!result.success) {
        return { success: false, error: result.error }
      }

      // Update store
      if (result.data) {
        configs.value.set(result.data.id, result.data)
        const resolved = await resolveConfigs([result.data])
        if (resolved.length > 0) {
          resolvedConfigs.value.set(resolved[0].id, resolved[0])
        }
      }

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Unlink a Spotify node from a scene
   */
  const unlinkFromScene = async (spotifyNodeId: string, sceneId: string): Promise<{ success: boolean; error?: string }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    try {
      const provider = getDataProvider()
      
      // Find the config first
      const config = getConfigForSceneAndNode.value(sceneId, spotifyNodeId)
      if (!config) {
        return { success: true } // Already unlinked
      }

      const result = await provider.delete('scene_spotify_configs', config.id)

      if (!result.success) {
        return { success: false, error: result.error }
      }

      // Remove from store
      configs.value.delete(config.id)
      resolvedConfigs.value.delete(config.id)

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Fetch all configs for the current user
   */
  const fetchAllConfigs = async (): Promise<{ success: boolean; error?: string }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    isLoading.value = true
    error.value = null

    try {
      const provider = getDataProvider()
      const allConfigs = await provider.query<SceneSpotifyConfig>('scene_spotify_configs')

      // Update store
      configs.value.clear()
      allConfigs.forEach(config => {
        configs.value.set(config.id, config)
      })

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      error.value = errorMessage
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  const clearConfigsForScene = (sceneId: string) => {
    const sceneConfigs = getConfigsForScene.value(sceneId)
    sceneConfigs.forEach(config => {
      configs.value.delete(config.id)
      resolvedConfigs.value.delete(config.id)
      loadingStates.value.delete(config.id)
    })
  }

  return {
    // State
    configs: computed(() => configs.value),
    resolvedConfigs: computed(() => resolvedConfigs.value),
    error: computed(() => error.value),
    isLoading: computed(() => isLoading.value),

    // Getters
    getConfigsForScene,
    getResolvedConfigsForScene,
    getConfigForSceneAndNode,
    getConfigById,
    isConfigLoading,

    // Actions
    fetchConfigsForScene,
    fetchAllConfigs,
    createConfig,
    updateConfig,
    deleteConfig,
    removeConfigForSceneAndNode,
    linkToScene,
    unlinkFromScene,
    clearConfigsForScene
  }
})